import { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Circle, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection, removeCreateDraft } from "./AuditSection";
import { showAlert } from "../lib/dialog";
import { confirmDeletePopover } from "../lib/confirmPopover";
import { getGbp } from "../lib/authClient";
import { siteKey, loadSiteInstructions } from "../lib/siteData";
import ParentSelect from "./ParentSelect";

// Service Area Buildout — pick a center address + radius, list the cities within it, then
// generate one publishable service-area/location page per city (SEO-optimized, first-person
// plural, H3 internal links to the site's service pages, one brand-anchored GBP link). The
// card→publish half mirrors the Content Drawer (AuditSection + snapshot/restore); the top half
// is the map + inputs. Per-site data lives under lsg_sa_* (in AUDIT_KEYS, so it swaps on switch).

interface Props {
  state: StateMap;
  siteUrl?: string;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
  getClient?: () => import("../lib/wpClient").WPClient | null;
}

interface Pin { city: string; state: string; lat: number; lng: number }
const RADII = [5, 10, 15, 20, 25, 30, 40, 50];
const ls = (k: string, d = "") => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const cityKey = (c: string, s: string) => `${c.trim().toLowerCase()}|${s.trim().toUpperCase()}`;

// The "Build service-area pages" run is tracked at MODULE level so it survives the component
// unmounting when the user switches tabs mid-build: the built cards are written straight to
// localStorage (not via setState → useEffect, which no-ops after unmount), and the mount effect
// re-attaches to restore the spinner and apply the result. Prevents a lost build / duplicate build.
type SaBuildResult = { ok: boolean; added: number; skipped: number; error?: string; effParent: string; effTags: string };
let saBuildInFlight: Promise<SaBuildResult> | null = null;
function saBuildStatus(r: SaBuildResult): string {
  if (!r.ok) return `⚠ ${r.error ?? "Failed to build pages"}`;
  if (r.added > 0) return `${r.added} service-area page${r.added === 1 ? "" : "s"} added${r.skipped ? `; ${r.skipped} skipped (URL already added)` : ""} — click ⚡ Create on each to generate & publish.`;
  return r.skipped ? `No pages added — ${r.skipped} ${r.skipped === 1 ? "city's URL is" : "cities' URLs are"} already in the list.` : "No cities to add.";
}

// Suite / unit designators break geocoding — "123 Main St, Ste 200, Boca Raton, FL" often fails
// to resolve, which stops the map (and the radius city lookup) from generating. Strip them before
// geocoding. Deliberately conservative: no bare "fl" (would eat the FL state), only clear unit
// designators, each removed WITH its identifier so street names aren't touched.
const SUITE_DES = "suite|ste|unit|apt|apartment|bldg|building|floor|rm|room|dept|department|pmb|spc|space|penthouse";
export function stripSuiteNumber(addr: string): string {
  const wholeSeg = new RegExp(`^(?:${SUITE_DES})\\b\\.?\\s*#?\\s*[a-z0-9-]+$`, "i"); // e.g. "Suite 200"
  const inline   = new RegExp(`\\s*\\b(?:${SUITE_DES})\\b\\.?\\s*#?\\s*[a-z0-9-]+`, "ig"); // "…St Ste 200"
  return addr
    .split(",")
    .map(s => s.trim())
    .map(seg => {
      if (wholeSeg.test(seg) || /^#\s*[a-z0-9-]+$/i.test(seg)) return ""; // whole "#200" / "Suite 200" segment
      return seg.replace(inline, "").replace(/\s*#\s*[a-z0-9-]+/g, "").replace(/\s{2,}/g, " ").trim();
    })
    .filter(Boolean)
    .join(", ");
}
const zoomForRadius = (miles: number) => (miles <= 7 ? 12 : miles <= 15 ? 11 : miles <= 30 ? 10 : 9);

// Leaflet's default marker asset URLs break under Vite; use lightweight divIcons instead.
const dotIcon = (color: string, big = false) => L.divIcon({
  className: "",
  html: `<div style="width:${big ? 16 : 12}px;height:${big ? 16 : 12}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.3)"></div>`,
  iconSize: [big ? 16 : 12, big ? 16 : 12],
  iconAnchor: [big ? 8 : 6, big ? 8 : 6],
});
const CENTER_ICON = dotIcon("#ea580c", true);
const CITY_ICON = dotIcon("#2563eb");
const CITY_ICON_OFF = dotIcon("#b6c0cf"); // unselected cities → greyed pin

// Great-circle miles between two [lat,lng] points (for the "X mi from HQ" hint in radius mode).
const milesBetween = (a: [number, number], b: [number, number]): number => {
  const R = 3958.8, rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b[0] - a[0]), dLng = rad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(s));
};

// Recenter the map imperatively when the address/radius changes (MapContainer's center prop is
// initial-only).
function FlyTo({ center, miles }: { center: [number, number] | null; miles: number }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, zoomForRadius(miles)); }, [center, miles, map]);
  return null;
}

export function ServiceAreaBuildout(props: Props) {
  const { connected, getClient } = props;
  const sk = props.siteUrl ? siteKey(props.siteUrl) : "";

  // ── Inputs (persisted per site via lsg_sa_*) ──
  const [address, setAddress] = useState(() => ls("lsg_sa_address"));
  const [radius, setRadius] = useState(() => { const n = parseInt(ls("lsg_sa_radius", "15"), 10); return RADII.includes(n) ? n : 15; });
  const [citiesText, setCitiesText] = useState(() => ls("lsg_sa_cities"));
  const [businessName, setBusinessName] = useState(() => ls("lsg_sa_business"));
  const [category, setCategory] = useState(() => ls("lsg_sa_category"));
  const [customTitle, setCustomTitle] = useState(() => ls("lsg_sa_custom_title") === "yes");
  const [titleFormat, setTitleFormat] = useState(() => ls("lsg_sa_title_fmt"));
  const [customServices, setCustomServices] = useState<Array<{ name: string; url: string }>>(() => {
    const blank = () => Array.from({ length: 5 }, () => ({ name: "", url: "" }));
    try { const v = JSON.parse(ls("lsg_sa_svc_list", "[]")); return Array.isArray(v) && v.length ? v : blank(); } catch { return blank(); }
  });
  const [addressHidden, setAddressHidden] = useState<"yes" | "no">(() => (ls("lsg_sa_addr_hidden") === "yes" ? "yes" : "no"));
  const [customersDrive, setCustomersDrive] = useState<"yes" | "no">(() => (ls("lsg_sa_drive") === "no" ? "no" : "yes"));
  const [disableMapEmbeds, setDisableMapEmbeds] = useState<"yes" | "no">(() => (ls("lsg_sa_no_map") === "yes" ? "yes" : "no"));
  // Parent Page + Page Tags to Assign are sourced ONLY from this site's Settings defaults
  // (Settings → Global Instructions). They are intentionally NOT independently persisted, so no
  // stale value can linger — if the Settings default is blank, these start blank.
  const [parentSlug, setParentSlug] = useState(() => ls("seo_default_sa_parent"));
  const [pageTags, setPageTags] = useState(() => ls("seo_default_sa_tags"));
  const [extraInstructions, setExtraInstructions] = useState(() => ls("lsg_sa_ci"));
  const [overwriteGlobal, setOverwriteGlobal] = useState(() => ls("lsg_sa_ci_overwrite") === "yes");
  const [center, setCenter] = useState<[number, number] | null>(() => { try { const v = JSON.parse(ls("lsg_sa_center", "null")); return Array.isArray(v) && v.length === 2 ? [Number(v[0]), Number(v[1])] as [number, number] : null; } catch { return null; } });
  const [pins, setPins] = useState<Pin[]>([]);
  // City → coordinates cache. Persisted to localStorage so that after a page refresh the map can
  // re-plot every city already in the list — otherwise this ref starts empty on reload and the
  // pins for a restored city list can't be drawn (they'd silently disappear). Lazily initialised
  // once (before effects run) so the mount-time pins effect sees the restored cache.
  const knownPins = useRef<Map<string, Pin>>(undefined as unknown as Map<string, Pin>);
  if (!knownPins.current) {
    knownPins.current = (() => {
      try {
        const arr = JSON.parse(ls("lsg_sa_known_pins", "[]")) as Pin[];
        if (Array.isArray(arr)) return new Map(arr.map(p => [cityKey(p.city, p.state), p]));
      } catch { /* ignore */ }
      return new Map<string, Pin>();
    })();
  }
  const saveKnownPins = useCallback(() => {
    try { localStorage.setItem("lsg_sa_known_pins", JSON.stringify([...knownPins.current.values()])); } catch { /* ignore */ }
  }, []);
  // How cities are gathered: "radius" (auto-find around the hub) or "manual" (paste a list).
  const [mode, setMode] = useState<"radius" | "manual">(() => (ls("lsg_sa_mode") === "manual" ? "manual" : "radius"));
  // Cities the user has UNCHECKED — only checked cities get pages built. Stored as deselected keys
  // so newly-added cities default to selected.
  const [deselected, setDeselected] = useState<Set<string>>(() => { try { const a = JSON.parse(ls("lsg_sa_deselected", "[]")); return new Set(Array.isArray(a) ? a as string[] : []); } catch { return new Set(); } });
  const [addCity, setAddCity] = useState("");

  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState("");
  const [parentOptions, setParentOptions] = useState<Array<{ value: string; label: string }>>([]);
  const gbpRef = useRef<{ gbpUrl: string; brand: string }>({ gbpUrl: "", brand: "" });

  useEffect(() => { try { localStorage.setItem("lsg_sa_address", address); } catch { /* ignore */ } }, [address]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_radius", String(radius)); } catch { /* ignore */ } }, [radius]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_cities", citiesText); } catch { /* ignore */ } }, [citiesText]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_mode", mode); } catch { /* ignore */ } }, [mode]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_deselected", JSON.stringify([...deselected])); } catch { /* ignore */ } }, [deselected]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_business", businessName); } catch { /* ignore */ } }, [businessName]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_category", category); } catch { /* ignore */ } }, [category]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_custom_title", customTitle ? "yes" : "no"); } catch { /* ignore */ } }, [customTitle]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_title_fmt", titleFormat); } catch { /* ignore */ } }, [titleFormat]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_svc_list", JSON.stringify(customServices)); } catch { /* ignore */ } }, [customServices]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_addr_hidden", addressHidden); } catch { /* ignore */ } }, [addressHidden]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_drive", customersDrive); } catch { /* ignore */ } }, [customersDrive]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_no_map", disableMapEmbeds); } catch { /* ignore */ } }, [disableMapEmbeds]);
  // Parent Page + Page Tags are NOT persisted here — they derive from the Settings defaults.
  useEffect(() => { try { localStorage.setItem("lsg_sa_ci", extraInstructions); } catch { /* ignore */ } }, [extraInstructions]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_ci_overwrite", overwriteGlobal ? "yes" : "no"); } catch { /* ignore */ } }, [overwriteGlobal]);
  useEffect(() => { try { localStorage.setItem("lsg_sa_center", JSON.stringify(center)); } catch { /* ignore */ } }, [center]);
  // One-time cleanup: older builds persisted Parent Page / Page Tags independently. Remove any
  // stale values so they can't reappear — these now come solely from the Settings defaults.
  useEffect(() => { try { localStorage.removeItem("lsg_sa_parent"); localStorage.removeItem("lsg_sa_tags"); } catch { /* ignore */ } }, []);

  // ── Prefill from the connected site: brand, GBP category/url, parent options, page inventory ──
  useEffect(() => {
    if (!connected) return;
    let alive = true;
    (async () => {
      // GBP profile → prefill category + brand + build the maps URL for the in-content link.
      if (sk) {
        try {
          const d = await getGbp(sk);
          if (alive && d.profile) {
            const p = d.profile;
            const gbpUrl = p.cid ? `https://maps.google.com/?cid=${p.cid}` : (p.profileUrl || "");
            const brand = props.siteBrand || p.businessName || (ls("cover_business_name"));
            gbpRef.current = { gbpUrl, brand };
            setBusinessName(prev => prev || brand || "");
            setCategory(prev => prev || p.category || "");
          }
        } catch { /* ignore */ }
      }
      if (!businessName) setBusinessName(props.siteBrand || ls("cover_business_name"));
      // Site pages → parent dropdown + service-page inventory.
      const client = getClient?.();
      if (client) {
        try {
          const pages = (await client.getAllPagesAndPosts()).filter(p => p.type === "page");
          if (!alive) return;
          const opts = pages.map(p => {
            let label = p.slug || String(p.url);
            try { label = new URL(p.url).pathname.replace(/\/$/, "") || "/"; } catch { /* keep */ }
            return { value: p.slug, label };
          }).filter(o => o.value).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
          setParentOptions(opts);
        } catch { /* ignore */ }
      }
    })();
    return () => { alive = false; };
  }, [connected, sk]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseLines = useCallback((text: string) => text.split("\n").map(l => l.trim()).filter(Boolean).map(l => {
    const m = l.match(/^(.*?)[,\s]+([A-Za-z]{2})$/); // "City, ST" or "City ST"
    return m ? { city: m[1].trim(), state: m[2].toUpperCase() } : { city: l, state: "" };
  }), []);

  // Recompute the map pins to match the current city list. Known cities (from the radius lookup)
  // are instant; unknown lines are geocoded only when "Add custom cities" is on.
  const syncPins = useCallback(async () => {
    const lines = parseLines(citiesText);
    const next: Pin[] = [];
    const toGeocode: Array<{ city: string; state: string }> = [];
    for (const { city, state } of lines) {
      const k = cityKey(city, state);
      const known = knownPins.current.get(k);
      if (known) next.push(known);
      else if (city) toGeocode.push({ city, state });
    }
    setPins(next);
    for (const { city, state } of toGeocode) {
      try {
        const q = state ? `${city}, ${state}, USA` : `${city}, USA`;
        const res = await fetch("/api/geocode", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ address: q }) });
        const data = await res.json() as { ok: boolean; lat?: number; lng?: number };
        if (data.ok && typeof data.lat === "number" && typeof data.lng === "number") {
          const pin = { city, state, lat: data.lat, lng: data.lng };
          knownPins.current.set(cityKey(city, state), pin);
          saveKnownPins();
          setPins(prev => [...prev, pin]);
        }
      } catch { /* skip this custom city */ }
      await new Promise(r => setTimeout(r, 900)); // Nominatim courtesy rate limit
    }
  }, [citiesText, parseLines, saveKnownPins]);

  // Recompute pins from known cities whenever the list changes (custom geocoding is on-demand
  // via the "Update Map Pins" button to avoid geocoding on every keystroke).
  useEffect(() => {
    const lines = parseLines(citiesText);
    setPins(lines.map(({ city, state }) => knownPins.current.get(cityKey(city, state))).filter((p): p is Pin => !!p));
  }, [citiesText, parseLines]);

  async function locate() {
    if (!address.trim()) { void showAlert("Enter a business address first.", { title: "No address" }); return; }
    // Strip suite/unit numbers first — they routinely break geocoding and leave the map empty.
    const cleanAddress = stripSuiteNumber(address);
    // Only blame suite numbers if the address actually HAD one. Otherwise a plain geocode failure
    // (e.g. the free geocoder is momentarily rate-limited, or the address is mistyped) must not
    // show a misleading "remove suite number" popup — surface the real reason instead.
    const hadSuite = cleanAddress.replace(/\s+/g, " ").trim().toLowerCase() !== address.replace(/\s+/g, " ").trim().toLowerCase();
    const onNoPins = (reason?: string) => {
      if (hadSuite) {
        void showAlert(
          "No map pins could be generated. Please remove the suite / unit number from the Business Address field (e.g. “Ste 200”, “Unit 5”, “#3”) and try again.",
          { title: "Remove suite number" },
        );
      } else {
        void showAlert(
          `${reason || "That address couldn’t be located."} The map/geocoding service may be momentarily busy — wait a few seconds and try again, or double-check the address.`,
          { title: "Couldn’t locate address" },
        );
      }
    };
    setLocating(true); setStatus("Locating address…");
    try {
      const res = await fetch("/api/geocode", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ address: cleanAddress }) });
      const data = await res.json() as { ok: boolean; lat?: number; lng?: number; error?: string };
      if (!data.ok || typeof data.lat !== "number" || typeof data.lng !== "number") {
        setStatus(`⚠ ${data.error ?? "Address not found"}`);
        onNoPins(data.error);
        return;
      }
      const c: [number, number] = [data.lat, data.lng];
      setCenter(c);
      const found = await loadCities(data.lat, data.lng, radius);
      if (!found) onNoPins("No cities were found near that address."); // geocoded, but no pins came back
    } catch (e) { const msg = e instanceof Error ? e.message : "Geocode failed"; setStatus(`⚠ ${msg}`); onNoPins(msg); }
    finally { setLocating(false); }
  }

  const loadCities = useCallback(async (lat: number, lng: number, miles: number): Promise<number> => {
    setStatus("Finding cities in radius…");
    try {
      const res = await fetch("/api/cities-in-radius", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lat, lng, miles }) });
      const data = await res.json() as { ok: boolean; cities?: Pin[]; error?: string };
      if (!data.ok || !data.cities) { setStatus(`⚠ ${data.error ?? "Lookup failed"}`); return 0; }
      for (const c of data.cities) knownPins.current.set(cityKey(c.city, c.state), { city: c.city, state: c.state, lat: c.lat, lng: c.lng });
      saveKnownPins();
      setCitiesText(data.cities.map(c => `${c.city}, ${c.state}`).join("\n"));
      setPins(data.cities.map(c => ({ city: c.city, state: c.state, lat: c.lat, lng: c.lng })));
      setStatus(`${data.cities.length} cit${data.cities.length === 1 ? "y" : "ies"} found within ${miles} miles.`);
      return data.cities.length;
    } catch (e) { setStatus(`⚠ ${e instanceof Error ? e.message : "Lookup failed"}`); return 0; }
  }, [saveKnownPins]);

  function onRadiusChange(next: number) {
    setRadius(next);
    if (center) void loadCities(center[0], center[1], next);
  }

  // ── Cards (mirrors Content Drawer's snapshot/restore + AuditSection publish flow) ──
  const [cardsHtml, setCardsHtml] = useState(() => ls("lsg_sa_cards"));
  const [building, setBuilding] = useState(false);
  const [history, setHistory] = useState<Array<{ id: number; title: string; link: string; type: string; created?: string }>>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const cardsRef = useRef<HTMLDivElement>(null);
  const SNAP_KEY = "lsg_sa_snapshot", SEQ_KEY = "lsg_sa_seq";
  const FIELD_IDS = ["title", "mt", "sl", "par", "md", "pt", "pd", "aft", "kw", "wc", "type", "ci", "summaryheading"] as const;
  useEffect(() => { try { localStorage.setItem("lsg_sa_cards", cardsHtml); } catch { /* ignore */ } }, [cardsHtml]);

  const snapshotCards = useCallback(() => {
    const root = cardsRef.current; if (!root) return;
    const snap: Record<string, unknown> = {};
    root.querySelectorAll<HTMLElement>('[id^="cft-content-"]').forEach(editor => {
      const fixId = editor.id.replace(/^cft-content-/, "");
      const fields: Record<string, string> = {};
      for (const f of FIELD_IDS) { const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`); if (el) fields[f] = el.value; }
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) fields.summarize = summCb.checked ? "1" : "";
      const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`);
      const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
      const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
      snap[fixId] = { fields, editor: editor.innerHTML, faq: editor.dataset.faqAnswer ?? "", open: row ? row.style.display !== "none" : false, contentShown: wrap ? wrap.style.display !== "none" : false, publish: submit ? submit.style.display !== "none" : false };
    });
    try { localStorage.setItem(SNAP_KEY, JSON.stringify(snap)); } catch { /* ignore */ }
  }, []);

  const restoreCards = useCallback(() => {
    const root = cardsRef.current; if (!root) return;
    let snap: Record<string, { fields?: Record<string, string>; editor?: string; faq?: string; open?: boolean; contentShown?: boolean; publish?: boolean }>;
    try { snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}"); } catch { return; }
    for (const [fixId, s] of Object.entries(snap)) {
      for (const f of FIELD_IDS) { const v = s.fields?.[f]; if (v == null) continue; const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`); if (el) el.value = v; }
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) { summCb.checked = s.fields?.summarize === "1"; const summWrap = root.querySelector<HTMLElement>(`#cft-summaryheading-wrap-${fixId}`); if (summWrap) summWrap.style.display = summCb.checked ? "block" : "none"; }
      const editor = root.querySelector<HTMLElement>(`#cft-content-${fixId}`);
      if (editor && s.editor) {
        editor.innerHTML = s.editor;
        if (s.faq) editor.dataset.faqAnswer = s.faq;
        const expand = root.querySelector<HTMLElement>(`.create-expand-btn[data-target="cfr-${fixId}"]`);
        if (expand) expand.dataset.autogenDone = "1";
        const gen = root.querySelector<HTMLElement>(`.generate-content-btn[data-fix-id="${fixId}"]`);
        if (gen) gen.textContent = "Regenerate Content";
      }
      if (s.contentShown) { const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`); if (wrap) wrap.style.display = ""; }
      if (s.open) { const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`); if (row) row.style.display = ""; }
      if (s.publish) { const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`); if (submit) submit.style.display = ""; }
    }
  }, []);

  const removeCard = useCallback((fixId: string) => {
    snapshotCards();
    try { const snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}"); delete snap[fixId]; localStorage.setItem(SNAP_KEY, JSON.stringify(snap)); } catch { /* ignore */ }
    removeCreateDraft(fixId); // deleting the card clears its saved draft (published cards keep theirs)
    setCardsHtml(prev => { const tmp = document.createElement("div"); tmp.innerHTML = prev; tmp.querySelector(`#hdr-${fixId}`)?.remove(); tmp.querySelector(`#sa-ctx-${fixId}`)?.remove(); tmp.querySelector(`#cfr-${fixId}`)?.remove(); return tmp.innerHTML.trim(); });
  }, [snapshotCards]);

  useEffect(() => () => snapshotCards(), [snapshotCards]);
  useEffect(() => { if (cardsHtml) restoreCards(); }, [cardsHtml, restoreCards]);

  useEffect(() => {
    const root = cardsRef.current; if (!root) return;
    root.querySelectorAll<HTMLElement>(".um-new .ac label.ac-chk").forEach(lbl => {
      if (!/ignore/i.test(lbl.textContent ?? "")) return;
      const prev = lbl.previousElementSibling;
      if (prev?.classList.contains("ac-sep")) prev.remove(); else if (lbl.nextElementSibling?.classList.contains("ac-sep")) lbl.nextElementSibling.remove();
      lbl.remove();
    });
    const cleanups: Array<() => void> = [];
    root.querySelectorAll<HTMLElement>(".cd-trash-btn").forEach(btn => {
      const fixId = btn.getAttribute("data-fix"); if (!fixId) return;
      const h = (e: Event) => { e.preventDefault(); confirmDeletePopover(btn, () => removeCard(fixId)); };
      btn.addEventListener("click", h); cleanups.push(() => btn.removeEventListener("click", h));
    });
    root.querySelectorAll<HTMLElement>(".um.um-new .um-hd").forEach(hd => {
      const onClick = (e: Event) => { if ((e.target as HTMLElement).closest(".ac, .cd-trash-btn, button, input, label, a")) return; hd.querySelector<HTMLButtonElement>(".create-expand-btn")?.click(); };
      hd.addEventListener("click", onClick); cleanups.push(() => hd.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach(fn => fn());
  }, [cardsHtml, removeCard]);

  async function createServiceAreas() {
    // Internal-link targets: the user's Service name + Service Page URL rows.
    const servicePages = customServices.filter(s => s.name.trim() && s.url.trim()).map(s => ({ url: s.url.trim(), title: s.name.trim() }));
    const servicesText = servicePages.map(p => p.title).join(", ");
    // Build a page for every BLUE pin on the map — the cities actually plotted (and, in radius
    // mode, still checked). This ties the build to what's visible on the current tab's map, so a
    // city list left in the other mode can't sneak in.
    const cities = bluePins.filter(p => p.city && p.state).map(p => ({ city: p.city, state: p.state }));
    if (!cities.length) {
      void showAlert(
        mode === "manual"
          ? "No blue pins on the map. Enter your cities, press “Update Map Pins” to plot them, then create."
          : "No blue pins on the map. Locate an address + radius to find nearby cities and tick the ones you want (checked cities show as blue pins).",
        { title: "No cities" },
      );
      return;
    }
    if (!category.trim()) { void showAlert("Enter the primary Google Business category.", { title: "Category required" }); return; }
    if (building || saBuildInFlight) return; // a build is already running (possibly from before a tab switch)
    setBuilding(true); setStatus("Building service-area pages…");
    // Capture live DOM slug edits NOW (while mounted); the stored-HTML regex inside the promise
    // covers the rest and remains valid after unmount.
    const domSlugs: string[] = [];
    cardsRef.current?.querySelectorAll<HTMLInputElement>('input[id^="cft-sl-sa-"]').forEach(i => { const v = i.value.trim().toLowerCase(); if (v) domSlugs.push(v); });
    // Run the whole build (fetch + persisting the new cards to localStorage) as a module-level
    // promise so it completes and saves even if this component unmounts on a tab switch.
    saBuildInFlight = (async () => {
      // This site's Global Instructions blob + per-site Parent/Tags defaults.
      let globalInstructions = "", defParent = "", defTags = "";
      if (sk) {
        try {
          const gi = await loadSiteInstructions(sk);
          if (gi.enabled && !overwriteGlobal) globalInstructions = gi.text.trim();
          defParent = (gi.defaults?.saParent ?? "").trim();
          defTags = (gi.defaults?.saTags ?? "").trim();
        } catch { /* ignore */ }
      }
      const effParent = parentSlug.trim() || defParent;
      const effTags = pageTags.trim() || defTags;
      // Index math + existing slugs read from localStorage (not React state / DOM) so they're
      // correct regardless of mount status.
      const existing = ls("lsg_sa_cards");
      const scannedMax = (() => { let max = -1; const re = /id="hdr-sa-(\d+)"/g; let m: RegExpExecArray | null; while ((m = re.exec(existing))) max = Math.max(max, parseInt(m[1], 10)); return max; })();
      let storedSeq = 0; try { storedSeq = parseInt(localStorage.getItem(SEQ_KEY) ?? "0", 10) || 0; } catch { /* ignore */ }
      const startIndex = Math.max(scannedMax + 1, storedSeq);
      const existingSlugs = new Set<string>(domSlugs);
      { const re = /id="cft-sl-sa-\d+"[^>]*value="([^"]*)"/g; let m: RegExpExecArray | null; while ((m = re.exec(existing))) { const v = m[1].trim().toLowerCase(); if (v) existingSlugs.add(v); } }
      try { const snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}"); for (let i = 0; i < cities.length; i++) delete snap[`sa-${startIndex + i}`]; localStorage.setItem(SNAP_KEY, JSON.stringify(snap)); } catch { /* ignore */ }
      const res = await fetch("/api/build-service-area-cards", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cities, startIndex, category: category.trim(), businessName: businessName.trim(),
          parentSlug: effParent, brand: (gbpRef.current.brand || businessName).trim(),
          gbpUrl: gbpRef.current.gbpUrl, servicePages, services: servicesText, pageTags: effTags,
          businessAddress: address.trim(), addressHidden: addressHidden === "yes", customersDrive: customersDrive === "yes",
          disableMapEmbeds: disableMapEmbeds === "yes",
          customTitle: customTitle && !!titleFormat.trim(), titleFormat: titleFormat.trim(),
          globalInstructions, extraInstructions: extraInstructions.trim(),
          existingSlugs: Array.from(existingSlugs),
        }),
      });
      const data = await res.json() as { ok: boolean; html?: string; added?: number; skipped?: number; error?: string };
      if (!data.ok) return { ok: false, added: 0, skipped: 0, error: data.error, effParent, effTags };
      const added = data.added ?? 0, skipped = data.skipped ?? 0;
      if (added > 0 && data.html) {
        try { localStorage.setItem(SEQ_KEY, String(startIndex + cities.length)); } catch { /* ignore */ }
        // Append straight to localStorage (re-read to fold in any concurrent card delete) so the
        // new cards persist even if the component already unmounted.
        try { const cur = ls("lsg_sa_cards"); localStorage.setItem("lsg_sa_cards", cur + data.html); } catch { /* ignore */ }
      }
      return { ok: true, added, skipped, effParent, effTags };
    })();
    void saBuildInFlight.catch(() => {}).finally(() => { saBuildInFlight = null; });
    try {
      const r = await saBuildInFlight;
      // Reflect the applied Parent/Tags defaults back into the visible fields.
      if (r.effParent !== parentSlug) setParentSlug(r.effParent);
      if (r.effTags !== pageTags) setPageTags(r.effTags);
      if (r.added > 0) setCardsHtml(ls("lsg_sa_cards"));
      setStatus(saBuildStatus(r));
    } catch (e) { setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`); }
    finally { setBuilding(false); }
  }

  // Re-attach to a "Build service-area pages" run still in flight from before a tab switch: restore
  // the spinner and apply the built cards (already written to localStorage) when it finishes.
  useEffect(() => {
    if (!saBuildInFlight) return;
    let alive = true;
    setBuilding(true); setStatus("Building service-area pages…");
    saBuildInFlight
      .then(r => { if (alive) { if (r.added > 0) setCardsHtml(ls("lsg_sa_cards")); setStatus(saBuildStatus(r)); } })
      .catch(e => { if (alive) setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`); })
      .finally(() => { if (alive) setBuilding(false); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── History — published service-area / location pages (durable _lsg_service_area meta) ──
  const loadHistory = useCallback(async () => {
    const client = getClient?.();
    if (!connected || !client) return;
    setHistoryLoading(true);
    try {
      const pages = await client.listServiceAreaPages();
      pages.sort((a, b) => (b.created ?? "").localeCompare(a.created ?? ""));
      setHistory(pages); setHistoryLoaded(true);
    } catch { /* leave previous */ }
    finally { setHistoryLoading(false); }
  }, [connected, getClient]);
  useEffect(() => { if (connected) void loadHistory(); }, [connected, loadHistory]);
  const fmtDate = (iso?: string) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); };

  // Dark-mode aware inline styling — the whole tab is inline-styled, so we branch on the theme
  // attribute the app stamps on <html>. Re-reads on every render (theme toggle re-renders App).
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const label: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: isDark ? "#93a4c0" : "#64748b", fontFamily: "'Inter','Mulish',sans-serif", marginBottom: 4 };
  const field: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "9px 11px", border: `1px solid ${isDark ? "rgba(255,255,255,.14)" : "#cbd5e1"}`, borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: isDark ? "#e2e8f0" : "#0f172a", outline: "none", background: isDark ? "rgba(0,0,0,.22)" : "#fff" };
  // Native <option>s default to transparent in some Chromium builds, causing a "missing white
  // strip at the bottom until you scroll" glitch on long dropdowns — set an explicit background.
  const optStyle: React.CSSProperties = { background: isDark ? "#1a2340" : "#ffffff", color: isDark ? "#e2e8f0" : "#0f172a" };
  const segWrap: React.CSSProperties = { display: "inline-flex", border: `1px solid ${isDark ? "rgba(255,255,255,.14)" : "#cbd5e1"}`, borderRadius: 0, overflow: "hidden", flexShrink: 0 };
  const seg = (on: boolean): React.CSSProperties => ({ padding: "7px 13px", border: "none", background: on ? "#2563eb" : (isDark ? "rgba(0,0,0,.22)" : "#fff"), color: on ? "#fff" : (isDark ? "#cbd5e1" : "#475569"), fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif", whiteSpace: "nowrap" });
  const linkBtn: React.CSSProperties = { border: "none", background: "none", color: "#2563eb", fontWeight: 700, fontSize: 11.5, cursor: "pointer", padding: 0, fontFamily: "'Inter','Mulish',sans-serif" };

  // ── City selection (checkbox list) ──
  const cityRows = parseLines(citiesText).filter(c => c.city);
  const isSel = (c: string, s: string) => !deselected.has(cityKey(c, s));
  const selectedCount = cityRows.filter(c => isSel(c.city, c.state)).length;
  // The BLUE pins currently on the map = every plotted pin that isn't greyed out. A pin is greyed
  // only in radius mode when its city is unchecked. Building from these makes the created cards
  // match exactly what the user sees on the map for the tab they're on (radius vs. manual), so a
  // city list left over in the other mode can't leak into the build.
  const isBlue = (c: string, s: string) => !(mode === "radius" && deselected.has(cityKey(c, s)));
  const bluePins = pins.filter(p => isBlue(p.city, p.state));
  const toggleSel = (c: string, s: string) => setDeselected(prev => { const n = new Set(prev); const k = cityKey(c, s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectAllCities = () => setDeselected(new Set());
  const clearAllCities = () => setDeselected(new Set(cityRows.map(c => cityKey(c.city, c.state))));
  const addCityLine = () => {
    const v = addCity.trim(); if (!v) return;
    const seen = new Set(cityRows.map(c => cityKey(c.city, c.state)));
    const parsed = parseLines(v)[0];
    if (parsed && !seen.has(cityKey(parsed.city, parsed.state))) setCitiesText(t => (t.trim() ? t.replace(/\s+$/, "") + "\n" : "") + v);
    setAddCity("");
  };
  const distMi = (c: string, s: string): number | null => {
    if (mode !== "radius" || !center) return null;
    const pin = knownPins.current.get(cityKey(c, s));
    return pin ? Math.round(milesBetween(center, [pin.lat, pin.lng])) : null;
  };

  return (
    <div className="content-drawer">
      <div className="sh"><h2>Service Area Buildout</h2><p style={{ whiteSpace: "nowrap", maxWidth: "none", fontSize: 11 }}>Generate SEO-optimized local service-area / location pages for the cities you serve.</p></div>

      <div className="cd-section">
        <div className="cd-section-hd"><span className="cd-hd-brand">Service Area</span></div>

        {/* How to gather cities: auto by radius, or a manual paste list */}
        <div style={{ marginBottom: 14 }}>
          <div style={segWrap}>
            <button style={seg(mode === "radius")} onClick={() => setMode("radius")}>Use radius</button>
            <button style={seg(mode === "manual")} onClick={() => setMode("manual")}>Enter cities manually</button>
          </div>
        </div>

        {/* Workspace: controls on the left, live map on the right */}
        <div className="sa-split" style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            {mode === "radius" ? (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ flex: "2 1 170px" }}>
                    <label style={label}>Business Address <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#ea580c" }}>(Do Not Add Suite Number)</span></label>
                    <input style={field} value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St, City, ST — no suite/unit number" onKeyDown={e => { if (e.key === "Enter") void locate(); }} />
                  </div>
                  <div style={{ flex: "0 0 118px" }}>
                    <label style={label}>Radius (miles)</label>
                    <select style={{ ...field, cursor: "pointer" }} value={radius} onChange={e => onRadiusChange(Number(e.target.value))}>
                      {RADII.map(r => <option key={r} value={r} style={optStyle}>{r} miles</option>)}
                    </select>
                  </div>
                </div>
                <button className="sa-btn" onClick={() => void locate()} disabled={locating}
                  style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", background: locating ? "#fdba74" : "#ea580c", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: locating ? "default" : "pointer", fontFamily: "'Inter','Mulish',sans-serif", whiteSpace: "nowrap" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.6A2.6 2.6 0 1 1 12 6.4a2.6 2.6 0 0 1 0 5.2z" /></svg>
                  {locating ? "Locating…" : "Locate & Find Cities"}
                </button>
              </>
            ) : (
              <div>
                <label style={label}>Paste your cities <span style={{ fontWeight: 400, textTransform: "none", color: "#94a3b8" }}>(one “City, ST” per line)</span></label>
                <textarea style={{ ...field, minHeight: 374, resize: "vertical", fontFamily: "'Roboto Mono', monospace", fontSize: 12, lineHeight: 1.6 }} value={citiesText} onChange={e => setCitiesText(e.target.value)} placeholder={"Boca Raton, FL\nDelray Beach, FL\nBoynton Beach, FL"} spellCheck={false} />
              </div>
            )}

            {/* City selection (checkboxes) — RADIUS mode only. In manual mode the paste box above
                is the list, so every city entered gets a page. */}
            {mode === "radius" && (
              <div style={{ marginTop: 10 }}>
                <label style={label}>Service Area / City Pages <span style={{ fontWeight: 400, textTransform: "none", color: "#94a3b8" }}>— check the cities to build pages for</span></label>
                <div style={{ border: `1px solid ${isDark ? "rgba(255,255,255,.12)" : "#e9edf3"}`, borderRadius: 8, overflow: "hidden" }}>
                  {cityRows.length > 0 ? (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 11px", background: isDark ? "rgba(0,0,0,.22)" : "#fff7f1", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,.12)" : "#f3e0d0"}`, fontSize: 11.5, color: isDark ? "#cbd5e1" : "#9a4a1a", fontWeight: 600 }}>
                        <span>{selectedCount} of {cityRows.length} selected</span>
                        <span style={{ marginLeft: "auto" }} />
                        <button onClick={selectAllCities} style={linkBtn}>Select all</button>
                        <span style={{ color: "#e0c4ad" }}>·</span>
                        <button onClick={clearAllCities} style={linkBtn}>Clear</button>
                      </div>
                      <div style={{ maxHeight: 240, overflowY: "auto" }}>
                        {cityRows.map((c, i) => {
                          const sel = isSel(c.city, c.state);
                          const d = distMi(c.city, c.state);
                          return (
                            <label key={`${cityKey(c.city, c.state)}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 11px", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,.07)" : "#eef1f6"}`, fontSize: 12.5, cursor: "pointer" }}>
                              <input type="checkbox" checked={sel} onChange={() => toggleSel(c.city, c.state)} style={{ width: 15, height: 15, accentColor: "#ea580c", cursor: "pointer", flex: "0 0 auto" }} />
                              <span style={{ fontWeight: sel ? 600 : 500, color: sel ? (isDark ? "#e2e8f0" : "#0f172a") : (isDark ? "#7c8aa0" : "#9aa6b6") }}>{c.city}{c.state ? `, ${c.state}` : ""}</span>
                              {d != null && <span style={{ marginLeft: "auto", fontFamily: "'Roboto Mono', monospace", fontSize: 10.5, color: "#94a3b8" }}>{d <= 0 ? "HQ" : `${d} mi`}</span>}
                            </label>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: "12px", fontSize: 12, color: "#94a3b8" }}>Locate an address and radius to find nearby cities.</div>
                  )}
                  <div style={{ borderTop: cityRows.length ? `1px solid ${isDark ? "rgba(255,255,255,.07)" : "#eef1f6"}` : "none" }}>
                    <input value={addCity} onChange={e => setAddCity(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCityLine(); } }} placeholder="+ Add a city (City, ST)…" style={{ width: "100%", boxSizing: "border-box", border: "none", padding: "8px 11px", fontSize: 12, fontFamily: "'Roboto Mono', monospace", color: isDark ? "#cbd5e1" : "#334155", outline: "none", background: isDark ? "rgba(0,0,0,.22)" : "#fff" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Update Map Pins — shown in both modes, aligned to the right of the controls column */}
            <button className="sa-btn" onClick={() => void syncPins()} style={{ alignSelf: "flex-end", padding: "6px 12px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 0, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>Update Map Pins</button>
          </div>

          {/* Live map (right) — radius ring only in radius mode; unselected cities show greyed */}
          <div className="sa-map" style={{ height: 440, borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0", position: "sticky", top: 12 }}>
            <MapContainer center={center ?? [39.5, -98.35]} zoom={center ? zoomForRadius(radius) : 4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FlyTo center={center} miles={radius} />
              {mode === "radius" && center && <>
                <Marker position={center} icon={CENTER_ICON} />
                <Circle center={center} radius={radius * 1609.34} pathOptions={{ color: "#ea580c", fillColor: "#ea580c", fillOpacity: 0.08 }} />
              </>}
              {pins.map((p, i) => <Marker key={`${cityKey(p.city, p.state)}-${i}`} position={[p.lat, p.lng]} icon={mode === "radius" && deselected.has(cityKey(p.city, p.state)) ? CITY_ICON_OFF : CITY_ICON} />)}
            </MapContainer>
          </div>
        </div>

        {/* Business details */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 12 }}>
          <div><label style={label}>Full Business Name</label><input style={field} value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Acme Plumbing Co." /></div>
          <div>
            <label style={label}>Primary Google Business Category
              <span className="audit-tip" style={{ marginLeft: 5, textTransform: "none" }}>
                <span className="audit-tip-icon">i</span>
                <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
                  This primary business category is used in the JSON-LD schema that is added to the service area pages automatically when published. It is also used as a prefix in the Page Title and Meta Title for each page. To customize the default prefix, toggle the <strong>Customize Page Title / Meta Title Prefix</strong> toggle and enter your custom Page Title / Meta Title format.
                </span>
              </span>
            </label>
            <input style={field} value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Plumber" />
          </div>

          {/* Customize Page Title / Meta Title Prefix */}
          <div style={{ gridColumn: "1 / -1", marginTop: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={customTitle} onChange={e => setCustomTitle(e.target.checked)} style={{ width: 15, height: 15, cursor: "pointer" }} />
                <span>Customize Page Title / Meta Title Prefix</span>
              </label>
              {customTitle && (
                <input
                  style={{ ...field, flex: "1 1 300px" }}
                  value={titleFormat}
                  onChange={e => setTitleFormat(e.target.value)}
                  placeholder="e.g. Affordable Plumbing in [City, St]"
                />
              )}
            </div>
            {customTitle && (
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                Use the <strong>[City, St]</strong> placeholder where the city &amp; state should appear (before or after your text). The Meta Title also appends “ | {businessName || "Business Name"}”.
              </div>
            )}
          </div>

          {/* Business Type Information — drives the map embed */}
          <div style={{ gridColumn: "1 / -1", marginTop: 10 }}>
            <label style={label}>Business Type Information</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155" }}>Is the business address hidden in the Google Business Profile?</span>
                <div style={segWrap}>
                  <button style={seg(addressHidden === "yes")} onClick={() => setAddressHidden("yes")}>Yes</button>
                  <button style={seg(addressHidden === "no")} onClick={() => setAddressHidden("no")}>No</button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155" }}>Do customers drive to the business?</span>
                <div style={segWrap}>
                  <button style={seg(customersDrive === "yes")} onClick={() => setCustomersDrive("yes")}>Yes</button>
                  <button style={seg(customersDrive === "no")} onClick={() => setCustomersDrive("no")}>No</button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
                <span style={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155" }}>Disable Map Embeds?</span>
                <div style={segWrap}>
                  <button style={seg(disableMapEmbeds === "yes")} onClick={() => setDisableMapEmbeds("yes")}>Yes</button>
                  <button style={seg(disableMapEmbeds === "no")} onClick={() => setDisableMapEmbeds("no")}>No</button>
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                {disableMapEmbeds === "yes" ? "Map embeds are disabled — no Google map or directions will be added to the service-area pages."
                  : addressHidden === "no" && customersDrive === "yes" ? "Each page embeds Google driving directions from the city to your business address."
                  : addressHidden === "no" && customersDrive === "no" ? "Each page embeds Google driving directions from your business address to the city."
                  : "Address hidden — each page embeds a Google map of the city instead of driving directions."}
              </div>
            </div>
          </div>

          {/* Services — name + URL rows (these become the H3 internal links) */}
          <div style={{ gridColumn: "1 / -1", marginTop: 24 }}>
            <label style={label}>Services Offered <span style={{ fontWeight: 400, textTransform: "none", color: "#94a3b8" }}>— entered as internal links (each an H3) on every page</span></label>
            <div style={{ display: "flex", gap: 8, marginTop: 10, marginBottom: 4 }}>
              <div style={{ flex: "1 1 180px" }}><span style={{ ...label, marginBottom: 0 }}>Service</span></div>
              <div style={{ flex: "2 1 260px" }}><span style={{ ...label, marginBottom: 0 }}>Service Page URL</span></div>
              <div style={{ width: 24 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {customServices.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input style={{ ...field, flex: "1 1 180px" }} value={s.name} onChange={e => setCustomServices(list => list.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="e.g. Drain Cleaning" />
                  <input style={{ ...field, flex: "2 1 260px" }} value={s.url} onChange={e => setCustomServices(list => list.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://example.com/drain-cleaning/" />
                  <button title="Remove service" onClick={() => setCustomServices(list => list.length > 1 ? list.filter((_, j) => j !== i) : [{ name: "", url: "" }])} style={{ width: 24, border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 17, lineHeight: 1, padding: 0 }}>×</button>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="sa-btn"
                  onClick={() => setCustomServices(list => [...list, { name: "", url: "" }])}
                  style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}
                >+ Add Service</button>
              </div>
            </div>
          </div>

          {/* Parent page */}
          <div style={{ gridColumn: "1 / -1", marginTop: 10 }}>
            {!connected && <div style={{ fontSize: 12, fontWeight: 700, color: "#ea580c", marginBottom: 6 }}>Connect to Website to populate your website's parent pages</div>}
            <label style={label}>Parent Page to Place Service Area / Location Pages</label>
            <ParentSelect
              value={parentSlug}
              onChange={setParentSlug}
              theme={isDark ? "dark" : "light"}
              width="100%"
              centered
              disabled={!connected}
              title="Select Parent Page"
              placeholder="— None (root) —"
              options={[
                { value: "", label: "— None (root) —" },
                ...parentOptions,
                // The Settings default is stored as a path (e.g. "/locations/"), which won't match a
                // fetched page-slug option — include it so it still displays as the selected value.
                ...(parentSlug.trim() && !parentOptions.some(o => o.value === parentSlug.trim())
                  ? [{ value: parentSlug.trim(), label: parentSlug.trim() }] : []),
              ]}
            />
          </div>

          {/* Page tags to assign */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={label}>Page Tags to Assign <span style={{ fontWeight: 400, textTransform: "none", color: "#94a3b8" }}>(comma-separated — prefilled into each page card's tags)</span></label>
            <input style={field} value={pageTags} onChange={e => setPageTags(e.target.value)} placeholder="e.g. Service Area, Location Page" />
          </div>

          {/* Custom Instructions — appended to each page card's Custom Instructions box */}
          <div style={{ gridColumn: "1 / -1", marginTop: 10 }}>
            <label style={label}>Custom Instructions <span style={{ fontWeight: 400, textTransform: "none", color: "#94a3b8" }}>(optional — prefilled into every service-area page card's Custom Instructions)</span></label>
            <textarea
              style={{ ...field, minHeight: 90, resize: "vertical", lineHeight: 1.5 }}
              value={extraInstructions}
              onChange={e => setExtraInstructions(e.target.value)}
              placeholder="e.g. Always mention our 24/7 support line and link to /contact/. Keep a warm, local tone."
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer", fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155" }}>
              <input type="checkbox" checked={overwriteGlobal} onChange={e => setOverwriteGlobal(e.target.checked)} style={{ width: 15, height: 15, cursor: "pointer" }} />
              <span>Overwrite existing new-page custom instructions currently in Settings for this website</span>
            </label>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              {overwriteGlobal
                ? "Each page card uses only the instructions above — this site's Settings new-page instructions are not included."
                : "The instructions entered above are appended after this site's new-page custom instructions (in Settings) on each page card by default."}
            </div>
          </div>
        </div>

        <div className="cd-actions" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
          {status && <span className="cd-status">{status}</span>}
          {/* Reflects the blue pins on the map — exactly the cities that will get a page. */}
          <span style={{ fontSize: 12, color: bluePins.length ? (isDark ? "#e2e8f0" : "#0f172a") : "#94a3b8", fontWeight: 600 }}>
            {bluePins.length} blue pin{bluePins.length === 1 ? "" : "s"} on the map → {bluePins.length} cit{bluePins.length === 1 ? "y" : "ies"} will be created
          </span>
          <button className="sa-btn" onClick={() => void createServiceAreas()} disabled={building || !connected}
            title={!connected ? "Connect your WordPress site first." : undefined}
            style={{ padding: "10px 18px", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, color: !connected ? "#64748b" : "#fff", cursor: (building || !connected) ? "default" : "pointer", background: !connected ? "#cbd5e1" : building ? "#93c5a3" : "#16a34a", fontFamily: "'Inter','Mulish',sans-serif" }}>{building ? "Building…" : "Create Service Areas →"}</button>
        </div>
        {!connected && <p className="cd-help" style={{ marginTop: 8, textAlign: "right" }}>Connect your WordPress site.</p>}
      </div>

      {cardsHtml && (
        <div className="cd-cards" ref={cardsRef}>
          <AuditSection
            id="servicearea" html={cardsHtml} state={props.state} siteBrand={props.siteBrand} auditDate={props.auditDate}
            onApprove={props.onApprove} onIgnore={props.onIgnore} onDone={props.onDone} onReset={props.onReset}
            hidden={props.hidden} onToggleHide={props.onToggleHide} connected={props.connected}
            fetchPageTags={props.fetchPageTags} onOpenSchemaSetup={props.onOpenSchemaSetup} getClient={props.getClient}
          />
        </div>
      )}

      {/* History — service-area / location pages published by the tool (found via the durable
          _lsg_service_area meta, so it survives the "LSG Service Area Page" tag being removed). */}
      <div className="cd-section cd-history-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">History - Published Service Area / Location Pages</span>
          <button className="cd-refresh-btn" onClick={() => void loadHistory()} disabled={historyLoading || !connected}>{historyLoading ? "Scanning…" : "↻ Refresh"}</button>
        </div>
        {!connected ? (
          <p className="cd-help">Connect your WordPress site to see the service-area pages this tool has published.</p>
        ) : history.length === 0 ? (
          <p className="cd-help">{historyLoaded ? "No service-area pages published by the tool on this site yet." : "Scanning the site…"}</p>
        ) : (
          <table className="cd-history">
            <thead><tr><th>Title</th><th>URL</th><th>Type</th><th>Uploaded</th></tr></thead>
            <tbody>
              {history.map(p => (
                <tr key={`${p.type}-${p.id}`}>
                  <td>{p.title || "(untitled)"}</td>
                  <td><a href={p.link} target="_blank" rel="noopener noreferrer">{p.link}</a></td>
                  <td style={{ textTransform: "capitalize" }}>{p.type}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(p.created)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
